"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}
type Step = {
  num: string;
  title: string;
  body: string;
};
type Props = {
  intro: string;
  steps: Step[];
};

// SVG path: a long vertical curvy thread, 4 control points
const THREAD_D = "M 50,20 C 20,180 80,360 40,540 C 20,720 80,880 50,1040 C 30,1180 70,1320 50,1480";
export function JourneyThread(t0) {
  const $ = _c(18);
  const {
    intro,
    steps
  } = t0;
  const rootRef = useRef(null);
  const pathRef = useRef(null);
  const drawRef = useRef(null);
  const bulletRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const root = rootRef.current;
      const path = pathRef.current;
      const draw = drawRef.current;
      const bullet = bulletRef.current;
      if (!root || !path || !draw || !bullet) {
        return;
      }
      const len = path.getTotalLength();
      draw.style.strokeDasharray = `${len}`;
      draw.style.strokeDashoffset = `${len}`;
      const ctx = gsap.context(() => {
        gsap.to(draw, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top 75%",
            end: "bottom 70%",
            scrub: 0.4
          }
        });
        const state = {
          t: 0
        };
        gsap.to(state, {
          t: 1,
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top 75%",
            end: "bottom 70%",
            scrub: 0.4,
            onUpdate: () => {
              const pt = path.getPointAtLength(state.t * len);
              bullet.setAttribute("cx", `${pt.x}`);
              bullet.setAttribute("cy", `${pt.y}`);
            }
          }
        });
        gsap.utils.toArray(".l2-journey-step").forEach(_temp);
      }, root);
      return () => ctx.revert();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      maxWidth: 1200,
      margin: "0 auto"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="l2-mono" style={{
      marginBottom: 24
    }}>[ PARCOURS ]</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      maxWidth: 900,
      marginBottom: 80
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== intro) {
    t6 = <h2 className="l2-display l2-display-lg" style={t5}>{intro}</h2>;
    $[5] = intro;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      position: "relative",
      display: "grid",
      gridTemplateColumns: "100px 1fr",
      gap: 40
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      position: "sticky",
      top: 0,
      width: 100,
      height: "100vh",
      maxHeight: "1500px",
      overflow: "visible"
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <svg viewBox="0 0 100 1500" preserveAspectRatio="xMidYMin meet" style={t8}><defs><filter id="l2-thread-glow"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter></defs><path ref={pathRef} d={THREAD_D} className="l2-thread-path" /><path ref={drawRef} d={THREAD_D} className="l2-thread-path-drawn" filter="url(#l2-thread-glow)" /><circle ref={bulletRef} cx="50" cy="20" r="5" className="l2-thread-bullet" /></svg>;
    t10 = {
      display: "flex",
      flexDirection: "column",
      gap: 200
    };
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] !== steps) {
    t11 = steps.map(_temp2);
    $[11] = steps;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11) {
    t12 = <div style={t7}>{t9}<div style={t10}>{t11}</div></div>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t12 || $[16] !== t6) {
    t13 = <section className="l2-section l2-journey" ref={rootRef}><div style={t3}>{t4}{t6}{t12}</div></section>;
    $[15] = t12;
    $[16] = t6;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  return t13;
}
function _temp2(s, i) {
  return <div key={i} className="l2-journey-step" style={{
    maxWidth: 560
  }}><p className="l2-mono" style={{
      marginBottom: 12
    }}>[ {s.num} ]</p><h3 className="l2-display" style={{
      fontSize: "clamp(32px, 4vw, 56px)",
      marginBottom: 16
    }}>{s.title}</h3><p style={{
      fontSize: 17,
      lineHeight: 1.55,
      color: "var(--l2-fg-dim)",
      maxWidth: 480
    }}>{s.body}</p></div>;
}
function _temp(el) {
  gsap.from(el, {
    opacity: 0,
    y: 30,
    duration: 1,
    ease: "expo.out",
    scrollTrigger: {
      trigger: el,
      start: "top 78%",
      once: true
    }
  });
}